import {
  IWedding,
  IWedding_Website,
  IWedding_Website_Section,
  IWedding_Website_Sections,
  IWedding_Website_Section_HotelsField,
  IWedding_Website_Section_PhotosField,
} from '@bridebook/models/source/models/Weddings.types';
import { IWeddingEvent } from '@bridebook/models/source/models/Weddings/Events.types';
import { IMAGES_BASE_URL } from '@bridebook/toolbox/src/buildImageUrl';
import { Box } from '@bridebook/ui';
import FooterTop from '@bridebook/web/app-shared/components/footer/footer-top/footer-top';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import styles from '@bridebook/web/components/wedding-website/view-mode/view-mode-website.style';
import {
  IViewModeContext,
  ViewModeContextProvider,
} from '@bridebook/web/components/wedding-website/view-mode/view-mode.context';
import { WeddingWebsiteMappedTile } from '@bridebook/web/components/wedding-website/view-mode/website-view/tiles/wedding-website-mapped-tile';
import { WeddingWebsiteOverview } from '@bridebook/web/components/wedding-website/view-mode/website-view/tiles/wedding-website-overview';
import { WeddingWebsiteSchedule } from '@bridebook/web/components/wedding-website/view-mode/website-view/tiles/wedding-website-schedule';
import { WeddingWebsiteWrappedTile } from '@bridebook/web/components/wedding-website/view-mode/website-view/tiles/wedding-website-wrapped-tile';
import { BBOrCustomSupplier } from '@bridebook/web/lib/supplier/hooks/query/use-supplier';
import {
  faqSection,
  giftListSection,
  hotelsSection,
  ourStorySection,
  photosSection,
  scheduleSection,
  venueSection,
} from '@bridebook/web/lib/wedding-website/constants/emptyWebsiteTemplate';
import { SECTION_NAMES } from '@bridebook/web/lib/wedding-website/constants';
import { Palettes } from '@bridebook/web/lib/wedding-website/types';
import { IThemedComponentStyles } from '@bridebook/web/lib/wedding-website/utils/get-themed-component-styles';
import { isPhotoWeddingSection } from '@bridebook/web/lib/wedding-website/utils/is-photo-wedding-section';
import {
  isGiftListSection,
  isPrimaryVenueSection,
} from '@bridebook/web/lib/wedding-website/utils/sections-typeguards';
import { shouldShowWebsiteSection } from '@bridebook/web/lib/wedding-website/utils/should-show-website-section';
import { compact } from 'lodash';
import partition from 'lodash/partition';
import { useMemo } from 'react';
import { useEditorScrollSync } from '../main/desktop/editor-sync-context';
import { isPhotoEmpty } from '../wedding-details/sections/photos/utils';
import { WebsiteTileVariant } from './partials/tile-component/wedding-website-tile-component';
import { WeddingWebsiteGiftList } from './website-view/tiles/wedding-website-gift-list';
import { WeddingWebsiteHotels } from './website-view/tiles/wedding-website-hotels';

type TEnrichedWebsiteSection = {
  section: IWedding_Website_Section;
  photoUrl?: string;
};

export const getPrimaryPhotosSection = (sections: IWedding_Website_Section[]) => {
  const photoSection = sections.find((section) => section.name === SECTION_NAMES.PRIMARY_PHOTOS);
  if (photoSection && isPhotoWeddingSection(photoSection)) return photoSection;
};

// export const extractWebsitePhotos = (section: IWedding_Website_Section_PhotosField) => {
//   const primarySectionPhotoUrls = section.data.map((photo) => `${IMAGES_BASE_URL}/${photo.path}`);

//   // we can iterate to create the names, but I wanted to preserve full asset names in code for ease of finding
//   const placeholderUrls = [
//     `${ASSET_PREFIX}/view-mode-placeholder-1.png`,
//     `${ASSET_PREFIX}/view-mode-placeholder-2.png`,
//     `${ASSET_PREFIX}/view-mode-placeholder-3.png`,
//   ];

//   // add placeholder photos until photo count is 3, unless already higher
//   return primarySectionPhotoUrls
//     .concat(placeholderUrls)
//     .slice(0, Math.max(primarySectionPhotoUrls.length, 3));
// };

// const getPrimaryVenueSection = (sections: IWedding_Website_Section[]) => {
//   const venueSection = sections.find((section) => section.name === 'primaryVenue');
//   if (venueSection && isVenueWeddingSection(venueSection)) return venueSection;
// };

export const extractWebsitePhotos = (section: IWedding_Website_Section_PhotosField) =>
  section.data.map((photo) =>
    isPhotoEmpty(photo) ? undefined : `${IMAGES_BASE_URL}/${photo.path}`,
  );

// Perhaps handle overview with the other sections?
export const enrichWebsiteSections = (
  sections: IWedding_Website_Section[],
): TEnrichedWebsiteSection[] => {
  const [[primaryPhotoSection], otherSections] = partition(
    sections,
    (section) => section.name === SECTION_NAMES.PRIMARY_PHOTOS,
  );
  if (!primaryPhotoSection || !isPhotoWeddingSection(primaryPhotoSection)) {
    return otherSections.map((section) => ({ section }));
  }
  const paddedPhotoUrls = extractWebsitePhotos(primaryPhotoSection);

  return otherSections.map<TEnrichedWebsiteSection>((section, index) => ({
    section,
    photoUrl: paddedPhotoUrls[index],
  }));
};

type Props = {
  websiteId?: string;
  suppliers: BBOrCustomSupplier[];
  sections: IWedding_Website_Sections;
  wedding: {
    date: IWedding['date'];
    partners: [string, string];
  };
  events: IWeddingEvent[];
  themeStyles: IThemedComponentStyles;
  editMode?: boolean;
  palette?: Palettes;
  config: IWedding_Website['config'];
  container?: IViewModeContext['container'];
  clickedSupplierProfileAnalytics?: () => void;
  showFooter?: boolean;
};

export const ViewModeWebsite = ({
  websiteId,
  wedding,
  sections,
  suppliers,
  themeStyles,
  editMode = false,
  container = 'page',
  palette,
  config,
  events,
  clickedSupplierProfileAnalytics,
  showFooter = true,
}: Props) => {
  const market = useMarket();
  useEditorScrollSync();
  const photos = useMemo(() => {
    const primaryPhotoSection = getPrimaryPhotosSection(sections) ?? photosSection;
    return extractWebsitePhotos(primaryPhotoSection);
  }, [sections]);
  const sectionsWithoutPhotos = useMemo(
    () => sections.filter((section) => section.name !== 'primaryPhotos'),
    [sections],
  );
  const primaryVenueId = sections.find(isPrimaryVenueSection)?.data.supplier;
  const paletteWithDefault = palette ?? Palettes.Bridebook;

  const ourStorySectionIndex = sectionsWithoutPhotos.findIndex(
    (section) => section.name === ourStorySection.name,
  );
  const venueSectionIndex = sectionsWithoutPhotos.findIndex(
    (section) => section.name === venueSection.name,
  );
  const faqSectionIndex = sectionsWithoutPhotos.findIndex(
    (section) => section.name === faqSection.name,
  );
  const giftListSectionIndex = sectionsWithoutPhotos.findIndex(
    (section) => section.name === giftListSection?.name,
  );
  const hotelsSectionIndex = sectionsWithoutPhotos.findIndex(
    (section) => section.name === hotelsSection.name,
  );
  const scheduleSectionIndex = sectionsWithoutPhotos.findIndex(
    (section) => section.name === scheduleSection.name,
  );

  const ourStorySectionData = sectionsWithoutPhotos?.[ourStorySectionIndex];
  const venueSectionData = sectionsWithoutPhotos?.[venueSectionIndex];
  const faqSectionData = sectionsWithoutPhotos?.[faqSectionIndex];
  const giftListSectionData = sectionsWithoutPhotos?.[giftListSectionIndex];
  const hotelsSectionData = sectionsWithoutPhotos?.[hotelsSectionIndex];
  const scheduleSectionData = sectionsWithoutPhotos?.[scheduleSectionIndex];
  const MappedSections = compact([
    <WeddingWebsiteOverview
      key="wedding-overview"
      websiteId={websiteId}
      palette={paletteWithDefault}
      primaryVenueId={primaryVenueId}
      config={config}
    />,
    shouldShowWebsiteSection(ourStorySectionData) ? (
      <WeddingWebsiteMappedTile
        key={ourStorySectionData.name}
        palette={paletteWithDefault}
        section={ourStorySectionData}
      />
    ) : null,
    shouldShowWebsiteSection(scheduleSectionData) && (
      <WeddingWebsiteSchedule
        key="wedding-schedule"
        palette={paletteWithDefault}
        events={events}
        weddingDate={wedding.date}
      />
    ),
    shouldShowWebsiteSection(venueSectionData) ? (
      <WeddingWebsiteMappedTile
        key={venueSectionData.name}
        palette={paletteWithDefault}
        section={venueSectionData}
      />
    ) : null,
    shouldShowWebsiteSection(faqSectionData) ? (
      <WeddingWebsiteMappedTile
        key={faqSectionData.name}
        palette={paletteWithDefault}
        section={faqSectionData}
      />
    ) : null,
    shouldShowWebsiteSection(giftListSectionData) && isGiftListSection(giftListSectionData) ? (
      <WeddingWebsiteGiftList
        key={giftListSectionData.name}
        palette={paletteWithDefault}
        section={giftListSectionData}
        variant={WebsiteTileVariant.Primary}
      />
    ) : null,
    shouldShowWebsiteSection(hotelsSectionData) ? (
      <WeddingWebsiteHotels
        key={(hotelsSectionData as IWedding_Website_Section_HotelsField).name}
        palette={paletteWithDefault}
        section={hotelsSectionData as IWedding_Website_Section_HotelsField}
        primaryVenueId={primaryVenueId}
        weddingDate={wedding.date}
        variant={WebsiteTileVariant.Primary}
      />
    ) : null,
  ]);
  const WrappedSections = MappedSections.map((ms, i) => {
    const isEven = i % 2 === 0;
    const shouldFlipPhotoPosition = i % 4 !== 0;
    return (
      <WeddingWebsiteWrappedTile
        // antipattern, but that's the best we can get in a short timeframe
        key={i}
        photoUrl={isEven ? photos[Math.floor(i / 2)] : undefined}
        order={shouldFlipPhotoPosition ? 'photo-right' : 'photo-left'}>
        {ms}
      </WeddingWebsiteWrappedTile>
    );
  });

  return (
    <ViewModeContextProvider
      wedding={wedding}
      suppliers={suppliers}
      themeStyles={themeStyles}
      editMode={editMode}
      container={container}
      clickedSupplierProfileAnalytics={clickedSupplierProfileAnalytics}>
      <Box style={styles.container}>
        {WrappedSections}
        {showFooter && (
          <Box style={styles.desktopOnly}>
            <FooterTop market={market} />
          </Box>
        )}
      </Box>
    </ViewModeContextProvider>
  );
};
